import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SLUG_MAX_LENGTH, SLUG_PATTERN, normSlug, slugBlur } from '@/lib/slug';
import { classifyVideoUrl, VIDEO_PROVIDER_NAMES } from '@explorentia/shared';
import {
  api,
  errorMessage,
  POI_CATEGORIES,
  POI_CATEGORY_LABELS,
  ENTITY_STATUSES,
  ENTITY_STATUS_LABELS,
  type AdminLocation,
  type EntityStatus,
  type PoiCategory,
} from '@/lib/api';
import { TranslationsField } from '@/components/TranslationsField';
import { LocationPickerMap } from '@/components/LocationPickerMap';
import { OpeningHoursField } from '@/components/OpeningHoursField';
import { AccessibilityField } from '@/components/AccessibilityField';
import { LocationBadgeSection } from '@/components/LocationBadgeSection';
import { LocationImagesSection } from '@/components/ImagesSection';
import { LocationPerksSection } from '@/components/LocationPerksSection';
import { LocationQuizSection } from '@/components/LocationQuizSection';
import {
  TranslationsListField,
  type TranslationsList,
} from '@/components/TranslationsListField';

export const Route = createFileRoute('/_authed/locations/$locationId')({
  component: LocationDetailPage,
});

// Translations are accepted from the form with EN required and everything
// else optional. The API mirrors this contract.
const tr = z.object({
  en: z.string().min(1),
  el: z.string().optional(),
  es: z.string().optional(),
  de: z.string().optional(),
  it: z.string().optional(),
  ru: z.string().optional(),
  he: z.string().optional(),
  pl: z.string().optional(),
  uk: z.string().optional(),
  fr: z.string().optional(),
  ar: z.string().optional(),
});
const trNullable = z
  .object({
    en: z.string().default(''),
    el: z.string().optional(),
    es: z.string().optional(),
    de: z.string().optional(),
    it: z.string().optional(),
    ru: z.string().optional(),
    he: z.string().optional(),
    pl: z.string().optional(),
    uk: z.string().optional(),
    fr: z.string().optional(),
    ar: z.string().optional(),
  })
  .nullable()
  .optional();

const schema = z.object({
  shortId: z.string().min(1).max(32),
  // The public site addresses this point as /villages/<village>/<slug>/.
  // Empty means "fall back to the short ID", which is ugly but never broken.
  slug: z.preprocess(
    normSlug(),
    z
      .string()
      .max(SLUG_MAX_LENGTH, `At most ${SLUG_MAX_LENGTH} characters`)
      .regex(SLUG_PATTERN, 'Lowercase letters, digits and single hyphens')
      .refine((v) => v !== 'businesses', '"businesses" is reserved for the village business list')
      .or(z.literal('')),
  ),
  position: z.coerce.number().int().min(1),
  // M1 additions — see /admin/locations/:id Zod schema in api/routes/admin.ts
  category: z.enum(POI_CATEGORIES as [PoiCategory, ...PoiCategory[]]),
  status: z.enum(ENTITY_STATUSES as [EntityStatus, ...EntityStatus[]]),
  isRequired: z.boolean(),
  address: z.string().nullable().optional(),
  title: tr,
  subtitle: tr,
  story: tr,
  // Structured tips: locale -> list of bullets. Loosely typed here because
  // the editor component owns the shape; the API validates it properly.
  practicalTipsItems: z.record(z.array(z.string())).optional(),
  lat: z.coerce.number(),
  lng: z.coerce.number(),
  videoUrl: z.string().nullable().optional(),
  videoPoster: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

// Drop blank entries and empty locales before saving. An admin who clicks
// "Add tip" and then changes their mind shouldn't leave a stray bullet in the
// visitor app, and a list that ends up empty everywhere clears the section.
function pruneTips(
  items: FormValues['practicalTipsItems'],
): AdminLocation['practicalTipsItems'] {
  if (!items) return null;
  const out: Record<string, string[]> = {};
  for (const [locale, list] of Object.entries(items)) {
    const cleaned = (list ?? []).map((t) => t.trim()).filter(Boolean);
    if (cleaned.length > 0) out[locale] = cleaned;
  }
  return out.en?.length ? (out as AdminLocation['practicalTipsItems']) : null;
}

function LocationDetailPage() {
  const { locationId } = Route.useParams();
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: ['location', locationId],
    queryFn: () => api.getLocation(locationId),
  });

  const save = useMutation({
    mutationFn: (body: Partial<AdminLocation>) => api.updateLocation(locationId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['location', locationId] });
    },
  });

  if (q.isLoading || !q.data) return <div className="card text-slate-gray">Loading location…</div>;

  // M5 moved images and quiz to dedicated sections with their own fetches.
  // The location-detail call still returns them for back-compat with the
  // old read-only view; we just don't surface them here anymore.
  const { location } = q.data;

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/routes/$routeId"
          params={{ routeId: location.routeId }}
          className="btn-ghost -ml-3 mb-2"
        >
          ← Back to locations
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-h1">{location.title.en}</h1>
          <span className="rounded-full bg-light-gray px-2 py-0.5 font-mono text-caption text-slate-gray">
            {location.shortId}
          </span>
          <span className="rounded-full bg-tech-blue/10 px-2 py-0.5 text-caption font-semibold text-tech-blue">
            Step {location.position}
          </span>
          <span className="rounded-full bg-electric-cyan/10 px-2 py-0.5 text-caption font-semibold text-electric-cyan">
            {POI_CATEGORY_LABELS[location.category]}
          </span>
          {!location.isRequired ? (
            <span className="rounded-full bg-warning/15 px-2 py-0.5 text-caption font-semibold text-warning">
              Optional
            </span>
          ) : null}
          {location.status !== 'active' ? (
            <span className="rounded-full bg-error/10 px-2 py-0.5 text-caption font-semibold text-error">
              {ENTITY_STATUS_LABELS[location.status]}
            </span>
          ) : null}
        </div>
      </div>

      <LocationForm
        location={location}
        onSubmit={(v) => save.mutateAsync(v)}
        saving={save.isPending}
        saved={save.isSuccess}
        error={save.error ? errorMessage(save.error) : null}
      />

      {/* M5: image gallery editor (hero + ordered gallery). Replaces the
          M1 read-only listing; uploads go via the presigned S3 flow. */}
      <LocationImagesSection locationId={locationId} />

      {/* M5: quiz editor — per-POI multiple-choice questions with all 6
          locales managed through a single per-question form. */}
      <LocationQuizSection locationId={locationId} />

      {/* M5: badge editor — per-POI badge (one per location). Image
          uploaded via the same presigned flow as locations/perks. */}
      <LocationBadgeSection locationId={locationId} />

      {/* M3: per-POI perks editor — manages the rows of `perks` linked to
          this location. Visitor flows pick the first active perk for the
          POI, so the order here matters less than the active/inactive
          status. */}
      <LocationPerksSection locationId={locationId} />
    </div>
  );
}

function LocationForm({
  location,
  onSubmit,
  saving,
  saved,
  error,
}: {
  location: AdminLocation;
  onSubmit: (body: Partial<AdminLocation>) => Promise<unknown>;
  saving: boolean;
  saved: boolean;
  error: string | null;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      shortId: location.shortId,
      slug: location.slug ?? '',
      position: location.position,
      category: location.category,
      status: location.status,
      isRequired: location.isRequired,
      address: location.address ?? '',
      title: location.title,
      subtitle: location.subtitle,
      story: location.story,
      practicalTipsItems: location.practicalTipsItems ?? {},
      lat: location.lat,
      lng: location.lng,
      videoUrl: location.videoUrl ?? '',
      videoPoster: location.videoPoster ?? '',
    },
  });

  const lat = watch('lat');
  const lng = watch('lng');

  // Tell the admin, as they type, how the URL they pasted will actually be
  // played — a YouTube link becomes an embed, a Drive link plays nowhere.
  const videoUrlValue = watch('videoUrl')?.trim();
  const videoSource = videoUrlValue ? classifyVideoUrl(videoUrlValue) : null;

  // Structured jsonb rather than flat fields, so they live beside the form
  // instead of inside it; `dirty` tracks them so Save lights up for a change
  // that react-hook-form cannot see.
  const [hours, setHours] = useState(location.openingHours ?? null);
  const [access, setAccess] = useState(location.accessibility ?? null);
  const [structuredDirty, setStructuredDirty] = useState(false);

  const submit = async (values: FormValues) => {
    const body: Partial<AdminLocation> = {
      ...values,
      slug: values.slug || null,
      openingHours: hours,
      accessibility: access,
      address: values.address || null,
      // Blank entries are dropped rather than saved as empty bullets, and a
      // list with nothing left in it clears the section entirely.
      practicalTipsItems: pruneTips(values.practicalTipsItems),
      videoUrl: values.videoUrl || null,
      videoPoster: values.videoPoster || null,
    };
    await onSubmit(body);
    setStructuredDirty(false);
    reset(values);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="card space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label">Short ID (used as default QR code)</label>
          <input className="input font-mono" {...register('shortId')} />
          {errors.shortId ? (
            <p className="mt-1 text-caption text-error">{errors.shortId.message}</p>
          ) : null}
        </div>
        <div>
          <label className="label">Slug (public website URL)</label>
          <input className="input font-mono" placeholder="monastery-of-the-holy-cross" {...register('slug', slugBlur(setValue))} />
          {errors.slug ? (
            <p className="mt-1 text-caption text-error">{errors.slug.message}</p>
          ) : null}
        </div>
        <div>
          <label className="label">Position in route</label>
          <input className="input" type="number" min={1} {...register('position')} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="label">Category</label>
          <select className="input" {...register('category')}>
            {POI_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {POI_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" {...register('status')}>
            {ENTITY_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ENTITY_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex cursor-pointer items-center gap-2">
            <input type="checkbox" {...register('isRequired')} />
            <span className="text-body text-dark-navy">Required for route completion</span>
          </label>
        </div>
      </div>

      <div>
        <label className="label">Address (optional, free-text)</label>
        <input
          className="input"
          placeholder="e.g. 1 Linou St, Omodos"
          {...register('address')}
        />
      </div>

      <TranslationsField
        label="Title"
        required
        basePath="title"
        register={register}
        setValue={setValue}
        watch={watch}
      />
      <TranslationsField
        label="Subtitle"
        required
        basePath="subtitle"
        register={register}
        setValue={setValue}
        watch={watch}
      />
      <TranslationsField
        label="Story"
        multiline
        required
        basePath="story"
        register={register}
        setValue={setValue}
        watch={watch}
      />
      <TranslationsListField
        label="Practical tips (Good to know)"
        hint="One tip per entry — visitors read them as a bulleted list."
        value={(watch('practicalTipsItems') ?? {}) as TranslationsList}
        onChange={(next) =>
          setValue('practicalTipsItems', next as FormValues['practicalTipsItems'], {
            shouldDirty: true,
          })
        }
      />

      <OpeningHoursField
        value={hours}
        onChange={(next) => {
          setHours(next);
          setStructuredDirty(true);
        }}
      />

      <AccessibilityField
        value={access}
        onChange={(next) => {
          setAccess(next);
          setStructuredDirty(true);
        }}
      />

      <LocationPickerMap
        label="POI location on the map"
        value={{ lat: Number(lat) || 0, lng: Number(lng) || 0 }}
        onChange={(next) => {
          setValue('lat', next.lat, { shouldDirty: true });
          setValue('lng', next.lng, { shouldDirty: true });
        }}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label">Video URL</label>
          <input
            className="input"
            placeholder="https://… .mp4, or a YouTube / Vimeo link"
            {...register('videoUrl')}
          />
          <p className="mt-1 text-caption text-slate-gray">
            Optional. A YouTube or Vimeo link, or a direct link to an .mp4 file
            — one that plays on its own when you paste it into a browser.
          </p>
          {videoSource?.kind === 'embed' ? (
            <p className="mt-1 text-caption text-tech-blue">
              Plays as an embedded {VIDEO_PROVIDER_NAMES[videoSource.provider]}{' '}
              player. The poster below is not used for these.
            </p>
          ) : null}
          {videoSource?.kind === 'unplayable' ? (
            <p className="mt-1 rounded-lg border border-warning/40 bg-warning/10 p-2 text-caption text-dark-navy">
              This {videoSource.service} link isn’t a playable video — it
              points at a web page — so the visitor app will show the poster and
              nothing else. Use a link to a single YouTube or Vimeo video, or a
              direct link to the .mp4 file.
            </p>
          ) : null}
        </div>
        <div>
          <label className="label">Video poster URL</label>
          <input className="input" {...register('videoPoster')} />
          <p className="mt-1 text-caption text-slate-gray">
            Optional — leave it empty if you don’t have one. It’s the still
            image shown before the visitor presses play, and it is ignored for
            YouTube and Vimeo links.
          </p>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-caption text-error">
          {error}
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={(!isDirty && !structuredDirty) || saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && !isDirty ? (
          <span className="text-caption text-success">Saved ✓</span>
        ) : null}
      </div>
    </form>
  );
}
